import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Box,
  Stack,
  Avatar,
  Paper,
} from "@mui/material";
import {
  CheckCircle,
  CreditCard,
  CalendarToday,
  Tag,
  AttachMoney,
} from "@mui/icons-material";
import { green, grey } from "@mui/material/colors";

interface PaymentReceiptProps {
  paymentId: string;
  amount: string;
  currency: string;
  status: string;
  paymentMethod: string;
  date: string;
}

export default function PaymentReceipt({
  paymentId,
  amount,
  currency,
  status,
  paymentMethod,
  date,
}: PaymentReceiptProps) {
  const formatPaymentMethod = (method: string) => {
    return `•••• •••• •••• ${method.slice(-4)}`;
  };

  return (
    <Card
      elevation={8}
      sx={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        borderRadius: 3,
        overflow: "visible",
        mt: 2,
      }}
    >
      <CardContent sx={{ pt: 1 }}>
        {/* Amount Section */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: green[50],
            border: `1px solid ${green[100]}`,
            borderRadius: 2,
            textAlign: "center",
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <AttachMoney sx={{ color: green[600], mr: 0.5 }} />
            <Typography
              variant="h3"
              component="div"
              fontWeight="bold"
              color={green[700]}
            >
              {amount} {currency}
            </Typography>
          </Box>
          <Chip
            icon={<CheckCircle sx={{ fontSize: 16 }} />}
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            sx={{
              bgcolor: green[100],
              color: green[800],
              fontWeight: "medium",
              "& .MuiChip-icon": {
                color: green[600],
              },
            }}
          />
        </Paper>

        <Divider sx={{ mb: 1 }} />

        {/* Transaction Details */}
        <Stack spacing={3}>
          {/* Payment ID */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Tag sx={{ color: grey[600], mr: 1, fontSize: 20 }} />
              <Typography
                variant="body2"
                fontWeight="medium"
                color="text.secondary"
              >
                Payment ID
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", maxWidth: 200, ml: 2 }}>
              <Typography
                variant="caption"
                component="code"
                sx={{
                  bgcolor: grey[100],
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                }}
              >
                {paymentId}
              </Typography>
            </Box>
          </Box>

          {/* Payment Method */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CreditCard sx={{ color: grey[600], mr: 1, fontSize: 20 }} />
              <Typography
                variant="body2"
                fontWeight="medium"
                color="text.secondary"
              >
                Payment Method
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                color: "text.primary",
              }}
            >
              {formatPaymentMethod(paymentMethod)}
            </Typography>
          </Box>

          {/* Date & Time */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CalendarToday sx={{ color: grey[600], mr: 1, fontSize: 20 }} />
              <Typography
                variant="body2"
                fontWeight="medium"
                color="text.secondary"
              >
                Date & Time
              </Typography>
            </Box>
            <Typography variant="body2" color="text.primary">
              {date}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Footer */}
        <Box sx={{ textAlign: "center", pt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Keep this receipt for your records
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
            Questions? Contact support
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
